<template>
  <div class="scroll-time-line">
    <v-row>
      <v-col cols="12" v-show="$vuetify.breakpoint.xs">
        <v-select
            outlined
            ref="eventSelectionComboBox"
            v-model="toggleEventLog"
            :menu-props="{ bottom: true, offsetY: true }"
            item-text="text"
            :items="eventLogTypes"
            :label="$t('book/event:log.select-filter-criteria')">
        </v-select>
      </v-col>

      <!-- Filter Toggle Btns -->
      <v-col cols="12" v-show="$vuetify.breakpoint.smAndUp">
        <v-btn-toggle
            v-model="toggleEventLog"
            mandatory
            borderless
            background-color="transparent"
        >
          <v-btn height="36" value="ALL" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>select_all</v-icon>
            <span>{{ $t('common:all') }}</span>
          </v-btn>

          <v-btn height="36" value="DOCUMENT" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>description</v-icon>
            <span>{{ $t('common:document') }}</span>
          </v-btn>

          <!-- TODO uncomment when contract and role are added to eventLogType and implemented in backend -->
          <!-- <v-btn height="36" value="CONTRACT" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>developer_board</v-icon>
            <span>{{ $t('book/facility:facility.dashboard.activities.toggle-btns.contracts') }}</span>
          </v-btn> -->

          <v-btn height="36" value="USER" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>engineering</v-icon>
            <span>{{ $t('common:user') }}</span>
          </v-btn>

          <v-btn height="36" value="PROCESS" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>account_tree</v-icon>
            <span>{{ $t('common:process') }}</span>
          </v-btn>

          <v-btn height="36" value="FACILITY" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>apartment</v-icon>
            <span>{{ $t('common:facility') }}</span>
          </v-btn>

          <!-- INFO: if further event logs with images should be created, we may should add a new eventLogType in backend -->
          <v-btn height="36" value="PROCESS_STEP_FAILED_WITH_IMAGE" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>image</v-icon>
            <span>{{ $t('common:image_plural') }}</span>
          </v-btn>
          <v-btn height="36" value="TASK" color="primary" class="text-capitalize white--text mr-2 mb-5">
            <v-icon color="white" left>splitscreen</v-icon>
            <span>{{ $t('common:task_plural') }}</span>
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <v-timeline dense class="pt-0">
      <div class="my-scroller">
        <v-timeline-item v-for="item in data" :key="item.id" class="mr-5">
          <template v-slot:icon>
            <v-avatar title color="blue">
              <v-icon dark>
                {{ eventLogHelper.findIcon(item.eventLogEvent) }}
              </v-icon>
            </v-avatar>
          </template>
          <v-card class="elevation-3">
            <v-card-subtitle :class="isAdmin ? 'd-flex justify-space-between' : ''">
              <div>
                <p class="mb-0">{{ parseDate(item.creationDate) }}</p>
                <template v-if="showFacilityName">
                  <span class="font-weight-bold"> - {{ item.facilityName }} </span>
                </template>
              </div>
              <div v-if="isAdmin">
                <BaseButton
                    icon="close"
                    color="red"
                    :btn-text="$t('buttons:delete')"
                    text
                    lower-case
                    small-icon
                    @clickedButton="deleteActivity(item)"
                />
              </div>
            </v-card-subtitle>
            <v-card-text>
              <p v-html="generateText(item)"></p>
              <v-carousel
                  height="230"
                  v-show="item.documentations.length > 0"
                  class="carousel-control-small">
                <v-carousel-item
                    v-for="docu in item.documentations"
                    :key="docu.id"
                >
                  <div v-if="docu.src">
                    <div
                        :class="$vuetify.breakpoint.xsOnly ? 'pdf-preview-wrapper-mobile' : 'pdf-preview-wrapper'"
                        v-if="docu.fileType === 'application/pdf'">
                      <div :class="$vuetify.breakpoint.xsOnly ? 'pdf-preview-mobile' : 'pdf-preview'">
                        <pdf
                            ref="pdf"
                            :src="docu.src"
                            :class="$vuetify.breakpoint.xsOnly ? 'pdf-mobile' : 'pdf'"
                            @click.native="viewDocumentation(item.documentations, docu)"
                        >

                        </pdf>
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>

                    <div
                        :class="$vuetify.breakpoint.xsOnly ? 'img-preview-wrapper-mobile' : 'img-preview-wrapper'" v-else>
                      <div class="img-preview">
                        <v-img
                            :src="docu.src"
                            width="150"
                            @click="viewDocumentation(item.documentations, docu)"
                        />

                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="loader-wrapper mt-16" v-else>
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        class="mx-auto"
                    ></v-progress-circular>
                  </div>
                </v-carousel-item>
              </v-carousel>
            </v-card-text>
          </v-card>
        </v-timeline-item>
      </div>
    </v-timeline>

      <v-pagination
          v-if="data && data.length > 0"
          class="mt-3"
          v-model="options.page"
          :length="totalPages"
          :total-visible="7"
      />

    <!-- Additional Components -->
    <DocumentViewer ref="documentViewer"/>

    <ViewTaskDialog ref="taskReview" @closeDialog="closeWindow" facility-window />
  </div>
</template>

<script>

import DocumentViewer from '@/modules/core/components/DocumentViewer/DocumentViewer';
import ViewTaskDialog from '@/modules/book/views/task/ViewTaskDialog.vue';
import TaskBoardService from '@/modules/book/services/task-board-service';
import EventLogService from '@/modules/book/services/eventlog-service';
import BaseButton from '@/modules/core/components/Base/BaseButton';
import EventLogHelper from '@/modules/book/data/eventLogHelper';
import DateUtils from '@/modules/core/services/date-utils';
import Utils from '@/modules/core/services/utils';
import {mapGetters} from 'vuex';
import Vue from 'vue';

export default {
  name: "ScrollTimeLine",
  props: {
    showFacilityName: Boolean,
  },
  components: {
    ViewTaskDialog,
    BaseButton,
    DocumentViewer,
    pdf: () => import('vue-pdf')
  },

  data() {
    return {
      eventLogHelper: null,
      loaded: false,
      options: {
        itemsPerPage: 10,
        page: 1,
        sort: ["createdAt"],
        sortAsc: [true]
      },
      data: [],
      totalPages: null,
      toggleEventLog: 'ALL'
    }
  },
  computed: {
    ...mapGetters('auth', ['isAdmin']),
    eventLogTypes() {
      return [
        {text: this.$t("common:all"), value: "ALL"},
        {text: this.$t("common:document"), value: "DOCUMENT"},
        {text: this.$t("common:user"), value: "USER"},
        // {text: this.$t("book/event:log.CONTRACT"), value: "CONTRACT"},
        {text: this.$t("common:process"), value: "PROCESS"},
        {text: this.$t("common:facility"), value: "FACILITY"},
        {text: this.$t("common:image_plural"), value: "PROCESS_STEP_FAILED_WITH_IMAGE"},
      ];
    }
  },
  watch: {
    toggleEventLog() {
      clearTimeout(this.toggleTimer);
      this.toggleTimer = setTimeout(() => {
        this.options.page = 1;
        this.callTimeLineUpdate();
      }, 500);
    },
    options: {
      handler() {
        this.callTimeLineUpdate();
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.eventLogHelper = new EventLogHelper();
    this.$watch(
        () => this.$route.hash,
        () => {
          this.openTask(this.$route.hash);
        }
    );

    let taskId = this.$route.hash;
    taskId = taskId.split('=');

    if (this.checkTaskId(taskId)) {
      this.closeWindow();
    }
  },
  methods: {
    deleteActivity(item) {
      let idx = this.data.findIndex((listItem) => listItem.id === item.id);
      if (idx !== -1) {
         EventLogService.deleteFromAPI(item.id,
              () => {
                Utils.showSuccessSnackbar(this.$t('snackbar:delete-event-log-success'));
                Vue.delete(this.data, idx);
              }, () => {
                Utils.showFailureSnackbar(this.$t('snackbar:delete-event-log-failure'));
              });
      }
    },
    parseDate(value) {
      return DateUtils.parseDateTimeForDisplay(value);
    },
    viewDocumentation(documents, data) {
      this.$refs.documentViewer.initViewerById(documents, data.id, this.$route.params.id, data.documentType);
    },
    generateText(item) {
      switch (item.eventLogEvent) {
        case 'PROCESS_FINISHED':
          item.message = this.$t('book/event:log.PROCESS_FINISHED_TXT', [item.text1, item.text2])
          break;
        case 'USER_SET_TO_ROLE':
          item.message = this.$t('book/event:log.USER_SET_TO_ROLE_TXT', [item.text1, item.text2])
          break;
        case 'USER_REMOVED_FROM_ROLE':
          item.message = this.$t('book/event:log.USER_REMOVED_FROM_ROLE_TXT', [item.text1, item.text2])
          break;
        case 'USER_SENT_ROLE_INVITATION':
          item.message = this.$t('book/event:log.USER_SENT_ROLE_INVITATION_TXT', [item.text2, item.text1]);
          break;
        case 'USER_ACCEPTED_ROLE':
          item.message = this.$t('book/event:log.USER_ACCEPTED_ROLE_TXT', [item.text1, item.text2])
          break;
        case 'USER_REJECTED_ROLE':
          item.message = this.$t('book/event:log.USER_REJECTED_ROLE_TXT', [item.text1, item.text2])
          break;
        case 'FOLLOW_UP_STARTED':
          item.message = this.$t('book/event:log.FOLLOW_UP_STARTED_TXT', {ProcessName: item.text1})
          break;
        case 'TECHNICAL_DOCUMENTATION_UPLOAD':
          item.message = this.$t('book/event:log.TECHNICAL_DOCUMENTATION_UPLOAD_TXT', {name: item.text2})
          break;
        case 'FACILITY_STATE_UPLOAD':
          item.message = this.$t('book/event:log.FACILITY_STATE_UPLOAD_TXT')
          break;
        case 'PROCESS_FAILED':
          item.message = this.$t('book/event:log.PROCESS_FAILED_TXT', [item.text1, item.text2])
          break;
        case 'DOCUMENTATION_UPLOADED_TO_FACILITY':
          item.message = this.$t('book/event:log.DOCUMENTATION_UPLOADED_TO_FACILITY_TXT', [item.text1, item.text2])
          break;
        case 'EDITED_BY_USER':
          item.message = this.$t('book/event:log.EDITED_BY_USER_TXT', [item.text1, item.text2])
          break;
        case 'PROCESS_STEP_FAILED_WITH_IMAGE':
          item.message = this.$t('book/event:log.PROCESS_STEP_FAILED_WITH_IMAGE_TXT', [item.text1, item.text2, item.text3])
          break;
        case 'POA_GENERATED':
          item.message = this.$t('book/event:log.POA_GENERATED_TXT')
          break;
        case 'POA_RECEIVED_TO_FACILITY':
          item.message = this.$t('book/event:log.POA_RECEIVED_TO_FACILITY_TXT')
          break;
        case 'REPORT_GENERATED':
          item.message = this.$t('book/event:log.REPORT_GENERATED_TXT', [item.text1]);
          break;
        case 'REPORT_RECEIVED_TO_FACILITY':
          item.message = this.$t('book/event:log.REPORT_RECEIVED_TO_FACILITY_TXT', [item.text1])
          break;
        case 'MANUALLY_GENERATED_REPORT':
          item.message = this.$t('book/event:log.MANUALLY_GENERATED_REPORT_TXT', [item.text1])
          break;
        case 'GBU_INSPECTION_DONE':
          item.message = this.$t('book/event:log.GBU_INSPECTION_DONE_TXT', [item.text1])
          break;
        case 'GBU_REPORT_GENERATED':
          item.message = this.$t('book/event:log.GBU_REPORT_GENERATED_TXT', [item.text1, item.text2])
          break;
        case 'GBU_COST_ESTIMATE_GENERATED':
          item.message = this.$t('book/event:log.GBU_COST_ESTIMATE_GENERATED_TXT', [item.text1, item.text2] )
          break;
        case 'GBU_SUPPORT_REPORT_GENERATED':
          item.message = this.$t('book/event:log.GBU_SUPPORT_REPORT_GENERATED_TXT', [item.text1, item.text2])
          break;
        case 'ONBOARDING_DONE':
          item.message = this.$t('book/event:log.ONBOARDING_DONE_TXT', [item.text1])
          break;
        case 'TASK_DOC_ADD':
        case 'OFFER_ADD':
        case 'INVOICE_ADD':
        case 'TASK_DOC_REMOVE':
        case 'OFFER_REMOVE':
        case 'INVOICE_REMOVE':
        case 'FINISH_ORDER':
        case 'SUCCESS_TASK':
        case 'FAIL_TASK':
          item.message = this.getTaskMessage(item)
          break;
        default:
          item.message = ''
      }
      return item.message;
    },
    callTimeLineUpdate() {
      clearTimeout(this.timeLineUpdateTimer);
      this.timeLineUpdateTimer = setTimeout(() => {

        this.$emit('updateData', this.options, this.toggleEventLog);
      }, 50);
    },
    fillTimeLine(data) {
      if (data == null) {
        this.data = [];
      } else {
        this.data = data.content;
      }
      this.totalPages = data.totalPages;
    },
    getTaskMessage(item) {
      return '<p><a href="#task=' + item.referenceId + '">' + item.text1 +'</a></p>'
          + this.$t('book/event:log.' + item.eventLogEvent, {id:item.text2});
    },
    openTask(task) {
      let taskInfo = task.split('=');

      if (this.checkTaskId(taskInfo)) {
        TaskBoardService.readTask(
            taskInfo[1],
            (response) => {
              this.$refs.taskReview.openDialog(response.data);
            },
            () => {
              Utils.showFailureSnackbar(this.$t('snackbar:delete-event-log-failure'));
            }
        );
      }
    },
    checkTaskId(taskId) {
      return taskId.length > 0
          && taskId[0]
          && taskId[1]
          && taskId[1].match('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');
    },
    closeWindow() {
      let route = this.$router.resolve({
        name: 'FacilityActivityTab',
        params: {id: this.$route.params.id}
      });
      this.$router.push(route.href);
    }
  }
}
</script>
<style lang="scss">
.scroll-time-line {
  /**
 ****** Vuetify Carousel CSS changes ******
 */
  // left btn smaller
  .v-window__prev {
    .v-btn {
      height: 24px !important;
      width: 24px !important;

      .v-icon {
        font-size: 24px !important;
      }
    }
  }

  // right btn smaller
  .v-window__next {
    .v-btn {
      height: 24px !important;
      width: 24px !important;

      .v-icon {
        font-size: 24px !important;
      }
    }
  }

  // Bottom control panel smaller
  // if elements are under 6
  .carousel-control-small {
    .v-carousel__controls {
      height: 30px !important;
    }
  }

  /**
   ****** Normal screen sizes and up ******
   */

  .pdf-preview-wrapper {
    position: relative;
    width: 100px;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .pdf-preview {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 100px;
      cursor: pointer;

      .pdf {
        width: auto;
      }
    }
  }

  .img-preview-wrapper {
    position: relative;
    width: 150px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .img-preview {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 150px;
      cursor: pointer;
    }
  }

  .loader-wrapper {
    position: relative;
    width: 32px;
    margin-left: auto;
    margin-right: auto;
  }

  /**
   ****** MOBILE ******
   */
  .pdf-preview-wrapper-mobile {
    position: relative;
    width: auto;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .pdf-preview-mobile {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: auto;
      cursor: pointer;

      .pdf-mobile {
        width: auto;
      }
    }
  }

  .img-preview-wrapper-mobile {
    position: relative;
    width: auto!important;

    .img-preview-mobile {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: auto!important;
      cursor: pointer;
    }
  }

  // css to scroll infinite
  .my-scroller {
    height: 600px;
    width: 100%;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--v-greyTint-base);
      border-radius: 36px;
      border: none;
    }
  }

  @media (max-width: $xs) {
    /**
   ****** Vuetify Carousel CSS changes ******
   */
    // left btn smaller
    .v-window__prev {
      top: 170px;
    }

    // right btn smaller
    .v-window__next {
      top: 170px;
    }

    .v-carousel__controls {
      display: none;
    }
  }
}
</style>